import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map, tap, withLatestFrom } from "rxjs/operators";

import { ModelObject } from "@hermes/api-model-core";
import { SearchSuggestion } from "@hermes/api-model-shell";
import { LOCALE, Settings, StorageService } from "@hermes/core-app-core";
import { FlatCategories } from "@hermes/domains-shell-states-menu-state";
import {
    FeatureFlagFacade,
    NEWSEARCHTOOL_PRODUCT_TRACKING,
} from "@hermes/hermes-web-states-flipper-state";

import {
    formatAndFilterCategorySuggestions,
    formatProductSuggestions,
} from "../helpers/header-search.helper";

import { Suggestion } from "../model/suggestions";

import {
    HEADER_SEARCH_VERSION_STORAGE_KEY,
    SEARCH_QUERY_ID_STORAGE_KEY,
} from "./header-search.constants";

@Injectable()
export class HeaderSearchService {
    private locale = inject(LOCALE);
    private httpClient = inject(HttpClient);
    private settings = inject(Settings);
    private storageService = inject(StorageService);
    private featureFlagFacade = inject(FeatureFlagFacade);
    private localStorage = this.storageService.getLocalStorageInstance();
    private sessionStorage = this.storageService.getSessionStorageInstance();

    public getSuggestions(
        term: string,
        categories: FlatCategories,
    ): Observable<Suggestion[]> {
        return this.getSearchSuggestion(term).pipe(
            map((suggestions) => {
                const categorySuggestion = formatAndFilterCategorySuggestions(
                    suggestions.categories,
                    categories,
                );
                return [
                    ...categorySuggestion,
                    ...formatProductSuggestions(
                        this.locale.urlPrefix,
                        suggestions.products,
                        categorySuggestion.length,
                    ),
                ];
            }),
        );
    }

    private getSearchSuggestion(term: string): Observable<SearchSuggestion> {
        const params: Record<string, string> = {
            terms: term,
            locale: this.locale.code,
        };

        const version = this.localStorage?.getItem<string>(
            HEADER_SEARCH_VERSION_STORAGE_KEY,
        );

        if (version) {
            params["version"] = version;
        }

        return this.httpClient
            .get(`${this.settings.apiUrl}/search/suggest`, {
                params,
            })
            .pipe(
                map((response) =>
                    ModelObject.fromJsonData(response, SearchSuggestion),
                ),
                withLatestFrom(
                    this.featureFlagFacade.isActivated(
                        NEWSEARCHTOOL_PRODUCT_TRACKING,
                    ),
                ),
                tap(([searchSuggestion, isTrackingEnabled]) => {
                    if (searchSuggestion.version) {
                        this.localStorage?.setItem<string>(
                            HEADER_SEARCH_VERSION_STORAGE_KEY,
                            searchSuggestion.version,
                        );
                    }
                    if (isTrackingEnabled && searchSuggestion.queryId) {
                        this.sessionStorage?.setItem<string>(
                            SEARCH_QUERY_ID_STORAGE_KEY,
                            searchSuggestion.queryId,
                        );
                    }
                }),
                map(([searchSuggestion]) => searchSuggestion),
            );
    }
}
